'use client';

import Image from 'next/image';
import {Minus, Plus, Trash} from 'lucide-react';
import {useMemo, useState, type ReactNode, type ComponentType} from 'react';
import {cn} from '../../utils/cn';

const defaultPriceFormatter = (value: number) => {
  const numeric = Number.isFinite(value) ? value : 0;
  return `${numeric.toLocaleString('ru-RU')} ₽`;
};

export interface CartItemAttribute {
  label: string;
  value: string;
}

export interface CartItemCardItem {
  id: string | number;
  name: string;
  brand?: string | null;
  imageUrl?: string | null;
  imageAlt?: string | null;
  attributes?: CartItemAttribute[];
  size?: string | null;
  price: number | string;
  priceOld?: number | string | null;
  quantity: number;
}

type ButtonProps = {
  children?: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  'aria-label'?: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'outline' | 'ghost' | 'destructive';
  [key: string]: any;
};

type CheckboxProps = {
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  'aria-label'?: string;
  className?: string;
  [key: string]: any;
};

export interface CartItemCardProps {
  item: CartItemCardItem;
  isSelected: boolean;
  onSelectChange: (checked: boolean) => void;
  onIncrease: () => void;
  onDecrease: () => void;
  onRemove?: () => void;
  isIncreaseDisabled?: boolean;
  isDecreaseDisabled?: boolean;
  priceFormatter?: (value: number) => string;
  className?: string;
  fallbackImageUrl?: string;
  checkboxLabel?: string;
  removeAriaLabel?: string;
  decreaseAriaLabel?: string;
  increaseAriaLabel?: string;
  actions?: ReactNode;
  ButtonComponent?: ComponentType<ButtonProps>;
  CheckboxComponent?: ComponentType<CheckboxProps>;
}

const DefaultButton: ComponentType<ButtonProps> = ({children, onClick, disabled, 'aria-label': ariaLabel, className, size = 'md', variant = 'default', ...props}) => {
  const sizeClasses = {
    sm: 'h-8 px-3 text-xs',
    md: 'h-9 px-4',
    lg: 'h-10 px-6',
  };
  const variantClasses = {
    default: 'bg-primary text-primary-foreground hover:bg-primary/90',
    outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
    ghost: 'hover:bg-accent hover:text-accent-foreground',
    destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className={cn(
        'inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50',
        sizeClasses[size],
        variantClasses[variant],
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
};

const DefaultCheckbox: ComponentType<CheckboxProps> = ({checked, onCheckedChange, 'aria-label': ariaLabel, className, ...props}) => {
  const checkboxId = useMemo(() => Math.random().toString(36), []);
  return (
    <label htmlFor={checkboxId} className="inline-flex items-center cursor-pointer">
      <input
        type="checkbox"
        id={checkboxId}
        checked={checked}
        onChange={(e) => onCheckedChange?.(e.target.checked)}
        aria-label={ariaLabel}
        className="sr-only peer"
        {...props}
      />
      <div
        className={cn(
          'grid place-content-center h-4 w-4 shrink-0 rounded-sm border border-input bg-background shadow focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
          checked && 'bg-primary text-primary-foreground',
          className
        )}
        aria-hidden="true"
      >
        {checked && (
          <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
        )}
      </div>
    </label>
  );
};

export const CartItemCard = ({
  item,
  isSelected,
  onSelectChange,
  onIncrease,
  onDecrease,
  onRemove,
  isIncreaseDisabled = false,
  isDecreaseDisabled = false,
  priceFormatter = defaultPriceFormatter,
  className,
  fallbackImageUrl = '/placeholder.png',
  checkboxLabel = 'Выбрать товар',
  removeAriaLabel = 'Удалить товар',
  decreaseAriaLabel = 'Уменьшить количество',
  increaseAriaLabel = 'Увеличить количество',
  actions,
  ButtonComponent = DefaultButton,
  CheckboxComponent = DefaultCheckbox,
}: CartItemCardProps) => {
  const [hasImageError, setHasImageError] = useState(false);
  const quantity = useMemo(() => {
    const value = Number(item.quantity);
    return Number.isFinite(value) && value > 0 ? value : 1;
  }, [item.quantity]);
  const price = useMemo(() => {
    const value = Number(item.price);
    return Number.isFinite(value) && value >= 0 ? value : 0;
  }, [item.price]);
  const oldPrice = useMemo(() => {
    const value = Number(item.priceOld);
    return Number.isFinite(value) && value > 0 ? value : null;
  }, [item.priceOld]);
  const showOldPrice = useMemo(() => {
    if (oldPrice === null) return false;
    return oldPrice > price;
  }, [oldPrice, price]);
  const imageSrc = hasImageError ? fallbackImageUrl : item.imageUrl ?? fallbackImageUrl;
  const resolvedAlt = item.imageAlt ?? item.name;

  return (
    <div className={cn('py-6', className)}>
      <div className="flex flex-col gap-3">
        <div className="flex items-start justify-between gap-4 md:gap-6">
          <div className="flex w-full items-start gap-4 md:gap-6">
            <div className="relative shrink-0">
              <Image
                src={imageSrc}
                alt={resolvedAlt}
                width={96}
                height={96}
                className="h-20 w-20 rounded object-cover md:h-24 md:w-24"
                onError={() => setHasImageError(true)}
              />
              <div className="absolute left-1 top-1 rounded-full bg-white p-0.5 shadow-sm">
                <CheckboxComponent
                  aria-label={checkboxLabel}
                  checked={isSelected}
                  onCheckedChange={onSelectChange}
                />
              </div>
            </div>
            <div className="w-full">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  {item.brand && (
                    <div className="text-sm uppercase tracking-wider text-gray-400">{item.brand}</div>
                  )}
                  <p className="line-clamp-2 text-base font-medium md:text-lg">{item.name}</p>
                  {(item.attributes?.length || item.size) && (
                    <div className="mt-2 text-sm text-gray-500">
                      {item.attributes?.length ? (
                        item.attributes.map((attribute, index) => (
                          <span key={`${attribute.label}-${index}`}>
                            {attribute.label}: {attribute.value}
                            {index < item.attributes!.length - 1 ? ', ' : ''}
                          </span>
                        ))
                      ) : (
                        <>Размер: {item.size}</>
                      )}
                    </div>
                  )}
                </div>
                {(onRemove || actions) && (
                  <div className="flex items-center gap-2">
                    {actions}
                    {onRemove && (
                      <ButtonComponent
                        size="sm"
                        variant="ghost"
                        onClick={onRemove}
                        aria-label={removeAriaLabel}
                        className="h-9 w-9 p-0"
                      >
                        <Trash className="h-4 w-4" />
                      </ButtonComponent>
                    )}
                  </div>
                )}
              </div>

              <div className="mt-3 hidden items-center justify-between gap-4 md:flex">
                <div className="flex items-center gap-3">
                  <p className="text-xl font-semibold text-gray-900">
                    {priceFormatter(price * quantity)}
                  </p>
                  {showOldPrice && oldPrice !== null && (
                    <p className="text-gray-400 line-through">{priceFormatter(oldPrice)}</p>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <ButtonComponent
                    size="sm"
                    variant="outline"
                    disabled={isDecreaseDisabled}
                    onClick={onDecrease}
                    aria-label={decreaseAriaLabel}
                    className="h-9 w-9 p-0"
                  >
                    <Minus className="h-4 w-4" />
                  </ButtonComponent>
                  <span className="inline-flex h-9 min-w-[2.5rem] items-center justify-center rounded-full bg-gray-100 px-3 text-lg font-semibold text-gray-900">
                    {quantity}
                  </span>
                  <ButtonComponent
                    size="sm"
                    variant="outline"
                    disabled={isIncreaseDisabled}
                    onClick={onIncrease}
                    aria-label={increaseAriaLabel}
                    className="h-9 w-9 p-0"
                  >
                    <Plus className="h-4 w-4" />
                  </ButtonComponent>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-2 flex items-center justify-between gap-4 md:hidden">
          <div className="flex items-center gap-3">
            <p className="text-xl font-semibold text-gray-900">
              {priceFormatter(price * quantity)}
            </p>
            {showOldPrice && oldPrice !== null && (
              <p className="text-gray-400 line-through">{priceFormatter(oldPrice)}</p>
            )}
          </div>
          <div className="flex items-center gap-1">
            <ButtonComponent
              size="sm"
              variant="outline"
              disabled={isDecreaseDisabled}
              onClick={onDecrease}
              aria-label={decreaseAriaLabel}
              className="h-9 w-9 p-0"
            >
              <Minus className="h-4 w-4" />
            </ButtonComponent>
            <span className="inline-flex h-9 min-w-[2.5rem] items-center justify-center rounded-full bg-gray-100 px-3 text-lg font-semibold text-gray-900">
              {quantity}
            </span>
            <ButtonComponent
              size="sm"
              variant="outline"
              disabled={isIncreaseDisabled}
              onClick={onIncrease}
              aria-label={increaseAriaLabel}
              className="h-9 w-9 p-0"
            >
              <Plus className="h-4 w-4" />
            </ButtonComponent>
          </div>
        </div>
      </div>
    </div>
  );
};
